import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import { getLocationListData, getLocationDataById } from '../locations/mockData'
import {
  _getCCRDataById,
  getCompanyContactRoleListData,
} from '../selectLists/companyContactRoles/mockData'
import { _storeEntityDataListToLocalForage } from '../../modules/mockDataStorageManager'
import type { TEntityId, IEntityDataItem } from '~/types/models'
import type { ILocationCompanyContact } from '~/types/locationCompanyContact/models'
import { assertIsString } from '~/types/models'
import {
  getCompanyContactListData,
  _getCCTDataById,
} from '~/api/msw/api/companyContacts/mockData'
import { ensureUniquenessAndPropagateFlagChange } from '~/cynqueModules/ensureUniquenessAndPropagateFlagChange'
import { propagateFlagChangeAfterDelete } from '~/cynqueModules/propagateFlagChangeAfterDelete'

const locationCompanyContactListData =
  undefined as unknown as ILocationCompanyContact[]

export const getLocationCompanyContactListData = getListDataFunctionFactory<
  ILocationCompanyContact[]
>({
  listData: locationCompanyContactListData,
  storageKey: 'locationcompanycontacts',
  getStartingListData: getStartingLocationCompanyContactListData,
})

export function _getLCCDataById(
  id: TEntityId,
  list: ILocationCompanyContact[]
): ILocationCompanyContact {
  if (!list) {
    throw new Error(`_getTMData called with undefined/null list`)
  }
  const locationCompanyContact = list.find(
    (lcc: ILocationCompanyContact) => lcc.id === id
  )
  if (!locationCompanyContact) {
    throw new Error(`Can't find locationCompanyContact '${id}'`)
  }
  return locationCompanyContact
}

export async function getLocationCompanyContactDataById(id: TEntityId) {
  const list = await getLocationCompanyContactListData()
  return _getLCCDataById(id, list)
}

export async function getLocationCompanyContactListDataByCompanyId(
  id: TEntityId
) {
  const list = await getLocationCompanyContactListData()
  if (!list) {
    throw new Error(`locationList returned undefined/null`)
  }
  const locationCompanyContact = list.filter(
    (lcc: ILocationCompanyContact) => lcc.companyContact.company.id === id
  )
  if (!locationCompanyContact) {
    throw new Error(`locationCompanyContact returned undefined/null`)
  }
  return locationCompanyContact
}

export async function getLocationCompanyContactListDataByLocationId(
  id: TEntityId
) {
  const list = await getLocationCompanyContactListData()
  if (!list) {
    throw new Error(`locationList returned undefined/null`)
  }
  const locationComapnyContacts = list.filter(
    (lcc: ILocationCompanyContact) => lcc.location.id === id
  )
  if (!locationComapnyContacts) {
    throw new Error(`locationCompanyContact returned undefined/null`)
  }
  return locationComapnyContacts
}

export async function processAfterAddOrUpdateLocationCompanyContactListData({
  targetItem: locationCompanyContact,
  updatedEntityData,
  listData: locationCompanyContacts,
}: {
  targetItem: IEntityDataItem
  updatedEntityData: Partial<ILocationCompanyContact>
  listData: ILocationCompanyContact[]
}) {
  const fn =
    '[api/msw/api/locationCompanyContacts/mockData.ts]' +
    ' processAfterAddOrUpdateLocationCompanyContactListData |'
  console.debug(`${fn} Post-processing locationCompanyContactListData`)

  const locationId = locationCompanyContact.location.id as string // already tested for string
  const locationCompanyContactsOfLocation = locationCompanyContacts.filter(
    (lcc) => lcc.location.id === locationId
  )
  const location = await getLocationDataById(locationCompanyContact.location.id)

  for (const locationCompanyContactPropertyName of [
    'isPrimaryLcon',
    'isSecondaryLcon',
  ]) {
    if (!updatedEntityData.hasOwnProperty(locationCompanyContactPropertyName)) {
      continue
    }
    await ensureUniquenessAndPropagateFlagChange({
      callingMn: fn,
      sourceEntityId: locationCompanyContact.id,
      sourceEntityFlagPropertyName: locationCompanyContactPropertyName,
      sourceEntityArray: locationCompanyContactsOfLocation,
      sourceEntityData: updatedEntityData,
      targetEntity: location,
    })
    await _storeEntityDataListToLocalForage({
      entityKeyBase: 'locations',
      listData: getLocationListData(),
      callingFn: fn,
    })
  }
}

export async function processAfterDeleteLocationCompanyContactListData({
  deletedEntityData,
}: {
  deletedEntityData: ILocationCompanyContact
}) {
  const fn =
    '[api/msw/api/locationCompanyContacts/mockData.ts]' +
    ' processAfterDeleteLocationCompanyContactListData |'
  console.debug(`${fn} Post-processing locationCompanyContactListData`)

  const locationId = deletedEntityData.location.id
  assertIsString(locationId)
  const location = await getLocationDataById(locationId)

  for (const locationCompanyContactPropertyName of [
    'isPrimaryLcon',
    'isSecondaryLcon',
  ]) {
    if (!deletedEntityData[locationCompanyContactPropertyName]) {
      console.debug(
        `${fn} Delete does not affect ${locationCompanyContactPropertyName}.` +
          ` Not calling ensureUniquenessAndPropagateFlagChange.`
      )
      continue
    }
    console.debug(
      `${fn} Delete affects ${locationCompanyContactPropertyName}.` +
        ` Calling ensureUniquenessAndPropagateFlagChange.`
    )
    await propagateFlagChangeAfterDelete({
      callingMn: fn,
      sourceEntityFlagPropertyName: locationCompanyContactPropertyName,
      sourceEntityData: deletedEntityData,
      targetEntity: location,
    })
    await _storeEntityDataListToLocalForage({
      entityKeyBase: 'locations',
      listData: getLocationListData(),
      callingFn: fn,
    })
  }
}

export async function getStartingLocationCompanyContactListData(): Promise<
  ILocationCompanyContact[]
> {
  const cctList = await getCompanyContactListData()
  const ccrList = await getCompanyContactRoleListData()
  return [
    {
      id: 'XXLCC0001',
      hrid: 'LCC0001',
      location: { id: 'XXL0001', label: 'Boston' },
      companyContact: _getCCTDataById('XXCCT0001', cctList),
      role: _getCCRDataById('XXCCR0001', ccrList),
      isPrimaryLcon: true,
      isSecondaryLcon: false,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXLCC0003',
      hrid: 'LCC0003',
      location: { id: 'XXL0001', label: 'Boston' },
      companyContact: _getCCTDataById('XXCCT0002', cctList),
      role: _getCCRDataById('XXCCR0005', ccrList),
      isPrimaryLcon: false,
      isSecondaryLcon: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXLCC0004',
      hrid: 'LCC0004',
      location: { id: 'XXL0002', label: 'Big Mac' },
      companyContact: _getCCTDataById('XXCCT0031', cctList),
      role: _getCCRDataById('XXCCR0005', ccrList),
      isPrimaryLcon: true,
      isSecondaryLcon: false,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXLCC0005',
      hrid: 'LCC0005',
      location: { id: 'XXL0002', label: 'Big Mac' },
      companyContact: _getCCTDataById('XXCCT0017', cctList),
      role: _getCCRDataById('XXCCR0005', ccrList),
      isPrimaryLcon: false,
      isSecondaryLcon: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXLCC0006',
      hrid: 'LCC0006',
      location: { id: 'XXL0003', label: 'Target' },
      companyContact: _getCCTDataById('XXCCT0029', cctList),
      role: _getCCRDataById('XXCCR0004', ccrList),
      isPrimaryLcon: true,
      isSecondaryLcon: false,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXLCC0007',
      hrid: 'LCC0007',
      location: { id: 'XXL0003', label: 'Target' },
      companyContact: _getCCTDataById('XXCCT0030', cctList),
      role: _getCCRDataById('XXCCR0004', ccrList),
      isPrimaryLcon: false,
      isSecondaryLcon: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXLCC0008',
      hrid: 'LCC0008',
      location: { id: 'XXL0004', label: 'Lake' },
      companyContact: _getCCTDataById('XXCCT0029', cctList),
      role: _getCCRDataById('XXCCR0004', ccrList),
      isPrimaryLcon: true,
      isSecondaryLcon: false,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXLCC0009',
      hrid: 'LCC0009',
      location: { id: 'XXL0004', label: 'Lake' },
      companyContact: _getCCTDataById('XXCCT0030', cctList)!,
      role: _getCCRDataById('XXCCR0004', ccrList),
      isPrimaryLcon: false,
      isSecondaryLcon: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXLCC0010',
      hrid: 'LCC0010',
      location: { id: 'XXL0009', label: 'Waltham' },
      companyContact: _getCCTDataById('XXCCT0001', cctList)!,
      role: _getCCRDataById('XXCCR0001', ccrList),
      isPrimaryLcon: true,
      isSecondaryLcon: false,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXLCC0011',
      hrid: 'LCC0011',
      location: { id: 'XXL0009', label: 'Waltham' },
      companyContact: _getCCTDataById('XXCCT0002', cctList)!,
      role: _getCCRDataById('XXCCR0005', ccrList),
      isPrimaryLcon: false,
      isSecondaryLcon: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXLCC0012',
      hrid: 'LCC0012',
      location: { id: 'XXL0010', label: 'Sunset' },
      companyContact: _getCCTDataById('XXCCT0001', cctList)!,
      role: _getCCRDataById('XXCCR0001', ccrList),
      isPrimaryLcon: true,
      isSecondaryLcon: false,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXLCC0013',
      hrid: 'LCC0013',
      location: { id: 'XXL0010', label: 'Sunset' },
      companyContact: _getCCTDataById('XXCCT0002', cctList)!,
      role: _getCCRDataById('XXCCR0005', ccrList),
      isPrimaryLcon: false,
      isSecondaryLcon: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
  ]
}
